<template>
    <div class="buttons">
        <button
            v-if="isFullOrganizer && !isDeleted"
            class="button is-small"
            :disabled="registeredCompetitor.status.value === 1"
            @click="openEditPaymentModal(registeredCompetitor)"
        >
            <span class="icon">
                <i class="fas fa-dollar-sign"></i>
            </span>
            <span>{{ translate("admin_competitors.edit_payment") }}</span>
        </button>
        <button v-if="isDeleted" class="button is-small" @click="restoreCompetitor(registeredCompetitor.id)">
            <span class="icon">
                <i class="fa fa-undo"></i>
            </span>
            <span>{{ translate("common.restore") }}</span>
        </button>
        <DropdownButton v-if="!isDeleted">
            <template v-slot:trigger>
                <button class="button is-small" :disabled="registeredCompetitor.status.value === 1">
                    <span>{{ translate("common.options") }}</span>
                    <span class="icon">
                        <i class="fa fa-angle-down" />
                    </span>
                </button>
            </template>
            <template v-slot:content>
                <a class="dropdown-item" :href="urls.eventAdmin().specificUserOnListingPage(eventId, registeredCompetitor.userId)">
                    <span class="icon">
                        <i class="fa fa-user" />
                    </span>
                    <span>
                        {{ translate("admin_competitors.go_to_user") }}
                    </span>
                </a>

                <a class="dropdown-item" @click="openEditRegistrationModal">
                    <span class="icon">
                        <i class="fa fa-edit" />
                    </span>
                    <span>
                        {{ translate("common.edit") }}
                    </span>
                </a>
                <a class="dropdown-item" @click="openCopyToCategoryModal">
                    <span class="icon">
                        <i class="fa fa-copy" />
                    </span>
                    <span>
                        {{ translate("admin_competitors.copy_to_category") }}
                    </span>
                </a>

                <a class="dropdown-item" @click="openDeleteCompetitorModal" href="#">
                    <span class="icon">
                        <i class="fa fa-times" />
                    </span>
                    <span>
                        {{ translate("common.delete") }}
                    </span>
                </a>
            </template>
        </DropdownButton>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import DropdownButton from "../Common/DropdownButton.vue";
    import { translate } from "../Common/translations";
    import {
        eventBus,
        OPEN_COPY_TO_CATEGORY_MODAL,
        OPEN_DELETE_COMPETITOR_MODAL,
        OPEN_EDIT_PAYMENT_MODAL,
        OPEN_EDIT_REGISTRATION_MODAL,
    } from "./eventBus";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import eventCompetitorsApi from "../Api/eventCompetitors";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import urls from "../Urls/urls";
    import { CompetitorPaymentWithMetadata } from "../Api/payments";

    const props = defineProps<{
        eventId: number;
        registeredCompetitor: CompetitorPaymentWithMetadata;
        isFullOrganizer: boolean;
    }>();

    const store = useRegisteredCompetitorsStore();

    const isDeleted = computed(() => props.registeredCompetitor.status.deleted);

    function openDeleteCompetitorModal() {
        eventBus.$emit(OPEN_DELETE_COMPETITOR_MODAL, props.registeredCompetitor);
    }

    function restoreCompetitor(eventRegistrationId: number) {
        const message = `${translate("admin_competitors.are_you_sure_to_restore")} #${props.registeredCompetitor.id} ${props.registeredCompetitor.firstName} ${props.registeredCompetitor.lastName}`;
        if (!confirm(message)) {
            return;
        }

        return eventCompetitorsApi
            .restoreRegistration(props.eventId, eventRegistrationId)
            .then(() => {
                store.getRegisteredCompetitorsPage();
                successToast(translate("flash_messages.competitor_restored"));
            })
            .catch(handleError);
    }

    function openCopyToCategoryModal() {
        eventBus.$emit(OPEN_COPY_TO_CATEGORY_MODAL, props.registeredCompetitor);
    }

    function openEditRegistrationModal() {
        eventBus.$emit(OPEN_EDIT_REGISTRATION_MODAL, props.registeredCompetitor);
    }

    function openEditPaymentModal(competitor: CompetitorPaymentWithMetadata) {
        eventBus.$emit(OPEN_EDIT_PAYMENT_MODAL, competitor);
    }
</script>

<style scoped></style>
